import { Box, CircularProgress, Container } from "@mui/material";
import React, { useState, useRef, useEffect } from "react";
import { useParams } from "react-router-dom";
import { BgBox } from "../../../components/common/BgBox/BgBox";
import bg from "../../../assets/products/imgBgWomen.png";
import { useQuery, useInfiniteQuery } from "@tanstack/react-query";
import { productsService } from "../services/productService";
import { ProductGrid } from "../components/ProductGrid";
import { ProductDetailModal } from "../components/ProductDetailModal";
import { Product } from "../types";

const PER_PAGE = 10;

export const ProductsPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const [selectedProductId, setSelectedProductId] = useState<number | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const loadMoreRef = useRef<HTMLDivElement | null>(null);

  const { data: category, isLoading: isCategoryLoading } = useQuery({
    queryKey: ["category", slug],
    queryFn: () => productsService.getCategoryBySlug(slug!),
    enabled: !!slug,
  });

  const {
    data,
    isLoading: isProductsLoading,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: ["products", category?.id ?? "all"],
    queryFn: ({ pageParam = 1 }) =>
      productsService.getAllProducts({
        _fields: "id,name,price,images",
        per_page: PER_PAGE,
        page: pageParam,
        orderby: "menu_order",
        order: "asc",
        ...(category?.id ? { category: String(category.id) } : {}),
      }),
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) =>
      lastPage.length === PER_PAGE ? allPages.length + 1 : undefined,
    enabled: !slug || !!category,
  });

  const products = data?.pages.flat() ?? [];
  const isLoading = isCategoryLoading || isProductsLoading;

  // IntersectionObserver: ดักจับเมื่อ sentinel เข้ามาใน viewport
  useEffect(() => {
    const el = loadMoreRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      { threshold: 0 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage, products.length]);

  const handleProductClick = (product: Product) => {
    setSelectedProductId(product.id);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setTimeout(() => setSelectedProductId(null), 300);
  };

  if (isLoading) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: "rgba(255, 255, 255, 1)",
        }}
      >
        <CircularProgress sx={{ color: "black" }} />
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "rgba(255, 255, 255, 1)" }}>
      <Container maxWidth={false} disableGutters>
        <BgBox image={category?.image?.src || bg} title={category?.name?.toUpperCase() || "ALL"} subTitle="All Products" height={480} />
      </Container>
      <Container maxWidth={false}>
        {!error && products.length > 0 && (
          <Box sx={{ padding: { xs: '2em 1em', sm: '3em 3em', md: '4em 8em' } }}>
            <ProductGrid
              products={products}
              onProductClick={handleProductClick}
            />
          </Box>
        )}

        {/* Sentinel อยู่นอก conditional เสมอ เพื่อให้ ref พร้อมตั้งแต่ต้น */}
        <Box ref={loadMoreRef} sx={{ height: "1px" }} />

        {isFetchingNextPage && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress sx={{ color: "black" }} />
          </Box>
        )}
      </Container>
      <ProductDetailModal
        productId={selectedProductId}
        open={modalOpen}
        onClose={handleCloseModal}
      />
    </Box>
  );
};
